import {
  MemberProductLevelEnum,
  PayChannel,
  PaymentRecord,
  SubscriptionCapability,
} from "@features/api/memberApi";
import { CHANNEL_META } from "@utils/member/checkoutChannel";

/** 仅内容区 Props，用于 EdgenModalCreateAble 的 children（无需 isOpen） */
export interface ManageBillingModalContentProps {
  onClose: () => void;
  // 指定视图
  view?: "paymentHistory" | "subscriptionAction";
  // 是否允许返回
  allowBack?: boolean;
  // 取消订阅
  onCancelSubscription?: SubscriptionListProps["onCancel"];
  /** 埋点位置：PC 弹窗 / H5 管理页（EDG-552） */
  position?: SubscriptionListProps["position"];
}

// 订阅操作组件Props
export interface SubscriptionActionProps {
  onCancelSubscription: () => void; // 取消订阅回调
  className?: string;
  buttonClassName?: string;
}

/** 订阅列表单条展示项（用于 SubscriptionList） */
export interface SubscriptionListItem {
  /** 订阅状态 */
  status: string;
  /** 订阅ID */
  id: string;
  /** 支付渠道，用于展示名称与取消按钮文案 */
  pay_channel: string;
  /** 计划等级，如 Pro、Expert（部分接口不返回时允许缺省） */
  product_level?: MemberProductLevelEnum;
  /** 下次付款日期展示文案，如 Jan 30, 2025 */
  nextPaymentDate: string;
  /** 是否可取消（可点击取消按钮），否则按钮置灰 */
  canCancel?: boolean;
  /** 订阅管理链接 */
  links?: string;
  /**
   * 后端声明的管理能力（EDG-552）。**undefined = 老版本响应未下发**，管理 UI 回退按渠道的旧逻辑（AC-33）；
   * 空数组是有效值（PAYPAL / IAP 能力为空集）。
   */
  capabilities?: SubscriptionCapability[];
  /** 是否在周期末自动续费 */
  autoRenew?: boolean;
  /** 未解决的扣款问题 */
  paymentIssue?: "PAYMENT_FAILED" | null;
  /** 当前周期结束日期展示文案（"MMM DD, YYYY"；无则 "—"） */
  currentPeriodEndDate?: string;
}

/** 订阅列表组件 Props */
export interface SubscriptionListProps {
  /** 订阅项列表，为空时显示空状态 */
  items?: SubscriptionListItem[];
  /** 关闭续费 / 取消订阅：交给弹窗外的挽留 → 问卷流程 */
  onCancel?: (item: SubscriptionListItem) => void;
  /** 门户 / PayPal autopay / 应用商店（旧路径） */
  onManageSubscription?: (item: SubscriptionListItem) => void;
  /** 换卡（Slice 5 接入；未传时不渲染该项） */
  onUpdatePaymentMethod?: (item: SubscriptionListItem) => void;
  /** 埋点：pc_modal（PC 弹窗）/ h5_page（H5 管理页打开的弹窗） */
  position?: "pc_modal" | "h5_page";
}

// 付款历史组件Props
export interface PaymentHistoryProps {
  paymentHistory: PaymentRecord[]; // 付款历史记录
  className?: string;
}

export const PayChannelMap = {
  [PayChannel.WALLET]: "Wallet",
  [PayChannel.STRIPE]: "Stripe",
  [PayChannel.APPLE_IAP]: "Apple Pay",
  [PayChannel.GOOGLE_IAP]: "Google Pay",
  [PayChannel.PAYPAL]: "PayPal",
} as const;

/**
 * 订阅列表 / 付款历史的渠道名：web 下单渠道以 `CHANNEL_META.listLabel` 为准（会翻译，
 * 如 AIRWALLEX →「Card / 卡片」，fe-spec §7 R7 不展示收单方品牌）；其余渠道用品牌名表。
 */
export function getPayChannelLabel(channel?: string): string {
  if (!channel) {
    return "";
  }
  return (
    CHANNEL_META[channel as PayChannel]?.listLabel() ??
    PayChannelMap[channel as keyof typeof PayChannelMap] ??
    ""
  );
}
